import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartData, PlatformData } from "./useChartData";
import { CustomTooltip, PieTooltip } from "./ChartTooltips";
import { Info, Video } from "lucide-react";
import { PLATFORM_COLORS, getTikTokColor } from "./chartUtils";
import { TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { Card } from "@/components/ui/card";
import PlatformIcon from "@/components/Generic/PlatformIcon";
import { Tooltip as UITooltip } from "@/components/ui/tooltip";
import { formatNumber } from "@/utils/format";

interface AnalyticsChartsProps {
  isLoading: boolean;
  hasVideoAnalytics: boolean;
  chartData: ChartData;
  platforms?: PlatformData[];
}

export function AnalyticsCharts({
  isLoading,
  hasVideoAnalytics,
  chartData,
  platforms,
}: AnalyticsChartsProps) {
  return (
    <>
      {/* Video Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Total Views Over Time - Takes 2 columns */}
        <div className="lg:col-span-2">
          <Card className="p-6 min-h-[500px] h-full">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-primary">
                  Total Views Over Time
                </h3>
                <UITooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Shows views over time for approved videos from selected
                      campaigns.
                    </p>
                  </TooltipContent>
                </UITooltip>
              </div>
              <p className="text-sm text-primary/60 mb-4">
                Track views across different platforms
              </p>
            </div>

            {isLoading ? (
              <div className="h-96 flex items-center justify-center">
                <div className="text-primary/60">Loading analytics...</div>
              </div>
            ) : hasVideoAnalytics ? (
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData.viewsChart}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="opacity-30"
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => formatNumber(value)}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />

                    {platforms?.map((platform) => (
                      <Line
                        key={platform.platform}
                        type="monotone"
                        dataKey={platform.platform}
                        stroke={
                          platform.platform === "tiktok"
                            ? getTikTokColor()
                            : PLATFORM_COLORS[
                                platform.platform as keyof typeof PLATFORM_COLORS
                              ] || PLATFORM_COLORS.unknown
                        }
                        strokeWidth={3}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        name={platform.platform.toUpperCase()}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-96 flex items-center justify-center">
                <div className="text-center">
                  <Video className="h-12 w-12 text-primary/40 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-primary mb-2">
                    No Video Analytics
                  </h3>
                  <p className="text-sm text-primary/60">
                    No video analytics data available yet
                  </p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Views Distribution Pie Chart */}
        <Card className="p-6 min-h-[500px] h-full">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-primary">
                Views by Platform
              </h3>
              <UITooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    Shows distribution of views across platforms.
                  </p>
                </TooltipContent>
              </UITooltip>
            </div>
            <p className="text-sm text-primary/60 mb-4">
              Distribution across platforms
            </p>
          </div>

          {isLoading ? (
            <div className="h-96 flex items-center justify-center">
              <div className="text-primary/60">Loading analytics...</div>
            </div>
          ) : hasVideoAnalytics && chartData.pieChart.length > 0 ? (
            <>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData.pieChart}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={2}
                      dataKey="views"
                    >
                      {chartData.pieChart.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Legend */}
              <div className="space-y-2 mt-4">
                {chartData.pieChart.map((entry) => (
                  <div
                    key={entry.platform}
                    className="flex items-center justify-between text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: entry.color }}
                      />
                      <PlatformIcon platform={entry.platform} />
                      <span className="capitalize">{entry.platform}</span>
                    </div>
                    <span className="font-medium">{entry.percentage}%</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-96 flex items-center justify-center">
              <div className="text-center">
                <Video className="h-12 w-12 text-primary/40 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-primary mb-2">
                  No Platform Data
                </h3>
                <p className="text-sm text-primary/60">
                  No platform analytics data available yet
                </p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Videos Posted Over Time */}
      <Card className="p-6 min-h-[500px]">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-primary">
              Videos Posted Over Time
            </h3>
            <UITooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">
                  Shows video publishing activity over time for approved videos.
                </p>
              </TooltipContent>
            </UITooltip>
          </div>
          <p className="text-sm text-primary/60 mb-4">
            Track video publishing activity across platforms
          </p>
        </div>

        {isLoading ? (
          <div className="h-96 flex items-center justify-center">
            <div className="text-primary/60">Loading analytics...</div>
          </div>
        ) : hasVideoAnalytics ? (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData.videosChart}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => formatNumber(value)}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />

                {platforms?.map((platform) => (
                  <Line
                    key={platform.platform}
                    type="monotone"
                    dataKey={platform.platform}
                    stroke={
                      platform.platform === "tiktok"
                        ? getTikTokColor()
                        : PLATFORM_COLORS[
                            platform.platform as keyof typeof PLATFORM_COLORS
                          ] || PLATFORM_COLORS.unknown
                    }
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    name={platform.platform.toUpperCase()}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-96 flex items-center justify-center">
            <div className="text-center">
              <Video className="h-12 w-12 text-primary/40 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-primary mb-2">
                No Video Data
              </h3>
              <p className="text-sm text-primary/60">
                No video analytics data available yet
              </p>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}
